import * as React from 'react';
import {useDispatch} from 'react-redux';

import {convertToArray, safelyGetFilterByKey} from 'nutshell-core/filter';

import {DashboardCard} from 'shells/dashboard/card/dashboard-card';
import {List} from 'shells/dashboard/list/list';
import {Illustration} from 'shells/illustrations/illustration';
import {PromoStateOverlay} from 'shells/promo-state-overlay/promo-state-overlay';

import {FormsReportChart} from '../../../forms/form-page/form-engagement/forms-report-chart';
import * as actions from '../../../forms/forms-actions';
import {OpenableModals, useOpenableModal} from '../../../openable-modals';
import type {ReportURLParams} from '../../../reports/report-types';

import {DEFAULT_TIMESPAN} from '../dashboard';
import {useGetFormsDashboard} from '../graphql/hooks';
import {mapViewSubmissionItemToListItem} from '../helpers';
import {ViewAllLink} from '../view-all-link';

import styles from './forms-card.module.css';

type Props = {
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function FormsCard(props: Props) {
    const dispatch = useDispatch();
    const activeFilters = convertToArray(props.urlParams.filter);
    const dateRangeFilter = safelyGetFilterByKey(activeFilters, 'period');

    const {formsDashboard, isLoading} = useGetFormsDashboard({
        timespan: dateRangeFilter || DEFAULT_TIMESPAN,
    });

    const formStats = formsDashboard ? formsDashboard.stats : [];
    const topForms = formsDashboard ? formsDashboard.topForms : [];
    const numForms = formsDashboard ? formsDashboard.numForms : undefined;
    const listItems = topForms.map((data) =>
        mapViewSubmissionItemToListItem({
            ...data.form,
            views: data.views,
            submissions: data.submissions,
        })
    );

    const {onOpen: onOpenFormsModal} = useOpenableModal(OpenableModals.FORMS_INFO_MODAL);

    // hardcoded since we're not using the url to feed this query
    const reportParams = {
        dateRangeType: 'period',
        facet: 'count',
        gap: 'd',
        segment: 'interactionType',
        formType: 0,
    } as const;

    React.useEffect(() => {
        dispatch(
            actions.requestFormEngagementReportData({
                reportParams,
                filters: activeFilters,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    return (
        <DashboardCard
            title='Forms'
            subtitle='Design and embed forms to collect leads from your website'
            link={<ViewAllLink totalCount={numForms} type='FORMS' />}
            isLoading={isLoading}
            mainContent={{
                statsProps: {stats: formStats, isHorizontal: true},
                content: (
                    <div className={styles.chart}>
                        <FormsReportChart
                            activeFilters={activeFilters}
                            height={246}
                            containerHeight={246}
                            onParamChange={props.onParamChange}
                            reportParams={reportParams}
                            isFullWidth={true}
                            hasBorder={false}
                            hasGap={false}
                        />
                    </div>
                ),
            }}
            sideContentProps={{
                content: (
                    <List
                        title='Top submission'
                        subtitle='Your most submitted forms'
                        listItems={listItems}
                        emptyStateText='No forms have been submitted in the selected timeframe'
                        shouldTruncate={true}
                    />
                ),
            }}
            promoStateOverlay={
                formsDashboard && formsDashboard.numForms === 0 ? (
                    <PromoStateOverlay
                        promoStateProps={{
                            isHorizontal: false,
                            imageComponent: (
                                <Illustration src='trial-onboarding/marketing-forms-chart' />
                            ),
                            title: 'Design and embed forms to collect leads from your website',
                            subtitle:
                                'Nutshell automatically tracks three metrics for form performance - page visits, submissions, and conversion rate.',
                            primaryCta: {
                                text: 'Learn more',
                                onClick: onOpenFormsModal,
                            },
                            secondaryCta: {
                                text: 'Create a form',
                                url: '/email-marketing/forms?view=board&sort=-modifiedTime',
                            },
                            blueImageBackground: true,
                        }}
                        align='right'
                    />
                ) : null
            }
        />
    );
}
